import { ApiError } from './errors';

/**
 * Parallelism for a user-triggered bulk write (approve all, create mappings, delete
 * selected, ...). The server admits only 4 core-web writes per tenant with a 0.25 s
 * fail-fast wait, and every other user in the company shares that budget, so one tab
 * running a bulk action sends its writes one at a time.
 */
export const BULK_WRITE_CONCURRENCY = 1;
/** Hard ceiling: a bulk action never takes more than half of the tenant write budget. */
export const BULK_WRITE_MAX_CONCURRENCY = 2;

export type BulkItemOutcome<T, R> =
  | { status: 'done'; item: T; value: R }
  | { status: 'failed'; item: T; error: unknown }
  /** Not sent: the batch stopped on a systemic error or was aborted first. */
  | { status: 'skipped'; item: T };

export interface BulkMutationResult<T, R> {
  /** One outcome per input item, in input order. */
  outcomes: Array<BulkItemOutcome<T, R>>;
  total: number;
  doneCount: number;
  failed: Array<{ item: T; error: unknown }>;
  skipped: T[];
  /** Failed and skipped items in input order: what a "retry" should send again. */
  remaining: T[];
  /** The systemic error that stopped the batch, or null when every item was attempted. */
  stopError: unknown;
}

export interface BulkMutationOptions {
  concurrency?: number;
  signal?: AbortSignal;
  onProgress?: (settled: number, total: number) => void;
}

function isAbortError(error: unknown): boolean {
  return (error instanceof DOMException || error instanceof Error) && error.name === 'AbortError';
}

/**
 * Errors that say nothing about the item and everything about the session or the
 * server: sending the next item would fail the same way and only add load, so the
 * batch stops and reports the rest as not sent. Item-level errors (409 stale version,
 * 422 validation, 404, 500 for one document) do not stop the batch.
 */
export function isBulkStoppingError(error: unknown): boolean {
  if (isAbortError(error)) return true;
  if (!(error instanceof ApiError)) return false;
  return (
    error.isTenantRuntimeBusy() ||
    error.isNetworkError() ||
    error.isSessionAuthError() ||
    error.isStaleAuthSessionResponse() ||
    error.statusCode === 502 ||
    error.statusCode === 503 ||
    error.statusCode === 504
  );
}

/** The user-facing reason for one failed item: the real error, never a guess. */
export function bulkFailureReason(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  return 'Неизвестная ошибка';
}

/**
 * Run one write per item with bounded concurrency and return every item's outcome.
 *
 * Each call goes through the shared API client, so a `429 tenant_runtime_busy` is
 * already retried there with the server's `Retry-After` (idempotency key required).
 * An item that still fails is recorded, and the batch continues with the next item,
 * unless the error is systemic (see `isBulkStoppingError`): then no new item starts
 * and the unsent ones are `skipped`. The function never rejects because of an item.
 */
export async function runBulkMutation<T, R>(
  items: readonly T[],
  mutate: (item: T) => Promise<R>,
  { concurrency = BULK_WRITE_CONCURRENCY, signal, onProgress }: BulkMutationOptions = {},
): Promise<BulkMutationResult<T, R>> {
  if (
    !Number.isInteger(concurrency) ||
    concurrency < 1 ||
    concurrency > BULK_WRITE_MAX_CONCURRENCY
  ) {
    throw new Error(
      `Bulk write concurrency must be an integer from 1 to ${BULK_WRITE_MAX_CONCURRENCY}`,
    );
  }
  const outcomes: Array<BulkItemOutcome<T, R> | undefined> = new Array(items.length);
  let nextIndex = 0;
  let settled = 0;
  // Systemic errors in the order they settled; the first one stops the batch.
  const stopErrors: unknown[] = [];

  const worker = async (): Promise<void> => {
    while (stopErrors.length === 0 && !signal?.aborted && nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      const item = items[index] as T;
      try {
        outcomes[index] = { status: 'done', item, value: await mutate(item) };
      } catch (error) {
        outcomes[index] = { status: 'failed', item, error };
        if (isBulkStoppingError(error)) stopErrors.push(error);
      }
      settled += 1;
      onProgress?.(settled, items.length);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => worker()));
  if (stopErrors.length === 0 && signal?.aborted && nextIndex < items.length) {
    stopErrors.push(new DOMException('Aborted', 'AbortError'));
  }

  const complete = items.map(
    (item, index): BulkItemOutcome<T, R> => outcomes[index] ?? { status: 'skipped', item },
  );
  const failed: Array<{ item: T; error: unknown }> = [];
  const skipped: T[] = [];
  const remaining: T[] = [];
  let doneCount = 0;
  for (const outcome of complete) {
    if (outcome.status === 'done') {
      doneCount += 1;
      continue;
    }
    remaining.push(outcome.item);
    if (outcome.status === 'failed') failed.push({ item: outcome.item, error: outcome.error });
    else skipped.push(outcome.item);
  }
  return {
    outcomes: complete,
    total: items.length,
    doneCount,
    failed,
    skipped,
    remaining,
    stopError: stopErrors[0] ?? null,
  };
}
